import { useEffect, useMemo } from "react";
import { AlertCircle, Loader2, RefreshCw } from "lucide-react";
import { useStateMachine } from "@/core/arch/state_machine";
import { Button } from "@/components/ui/base/button";
import { SignalsBloc } from "@/application/signals/signals_bloc";
import { StorageSignalsRepository } from "@/application/signals/signals_repository";
import type {
  Cohort,
  MerchantSignalRow,
  SignalsAggregates,
} from "@/application/signals/types";
import { SignalsCard, StatTile } from "./components/SignalsCard";
import { BarList, TrendBars, type BarItem } from "./components/charts";
import { tierColor } from "./tier-color";
import { ConversionCard } from "./components/ConversionCard";
import { StallMapCard } from "./components/StallMapCard";
import {
  AdvocateCandidatesCard,
  EmergingSegmentsCard,
} from "./components/OpportunityCards";
import { CohortPanel } from "./components/CohortPanel";
import {
  formatChannel,
  formatCount,
  formatCountry,
  formatDateTime,
  formatDays,
  formatGpv,
  formatMonth,
  formatPercent,
  formatTermSplit,
  formatTier,
  titleCase,
} from "./format";
import { planCohort } from "@/application/signals/cohort";

/**
 * The Signals observatory: how the merchant base is composed, how well each
 * part of it converts, and where growth is coming from.
 *
 * Everything on the page reads from a dated S3 artifact built by the scheduled
 * `signals-build` job — never from the live tables — so the page is cheap to
 * open and consistent between viewers. Every slice drills through to the
 * merchants behind it and exports as CSV.
 */
export function SignalsView() {
  const [state, bloc] = useStateMachine(
    () => new SignalsBloc(new StorageSignalsRepository()),
  );

  useEffect(() => {
    void bloc.load();
  }, [bloc]);

  const drill = (cohort: Cohort) => bloc.drillInto(cohort);

  return (
    <div>
      <header className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-foreground text-2xl font-bold">Dashboard</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Amplify merchant base
            {state.aggregates &&
              ` · built ${formatDateTime(state.aggregates.meta.generatedAt)}`}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          disabled={state.status === "loading"}
          onClick={() => void bloc.load()}
        >
          <RefreshCw
            className={`mr-1.5 h-4 w-4 ${
              state.status === "loading" ? "animate-spin" : ""
            }`}
          />
          Refresh
        </Button>
      </header>

      {state.status === "loading" && (
        <div className="bg-card text-muted-foreground flex items-center justify-center gap-2 rounded-lg border py-16 text-sm">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading the latest build…
        </div>
      )}

      {state.status === "empty" && (
        <div className="bg-card rounded-lg border px-6 py-16 text-center">
          <p className="text-foreground text-sm">No build available yet</p>
          <p className="text-muted-foreground mt-1 text-sm">
            The dashboard reads a dated artifact produced by the scheduled build
            job. The first run publishes it.
          </p>
        </div>
      )}

      {state.status === "error" && (
        <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-4">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />
          <div>
            <p className="text-sm font-medium text-red-900">
              Could not load the dashboard build
            </p>
            <p className="mt-0.5 text-sm text-red-700">{state.error}</p>
          </div>
        </div>
      )}

      {state.status === "loaded" && state.aggregates && (
        <SignalsBody
          aggregates={state.aggregates}
          rows={state.rows}
          onDrill={drill}
        />
      )}

      <CohortPanel
        cohort={state.cohort}
        rows={bloc.cohortRows}
        mccBySegment={state.aggregates?.mccBySegment ?? {}}
        generatedAt={state.aggregates?.meta.generatedAt ?? ""}
        onClose={() => bloc.clearCohort()}
        onDrill={drill}
      />
    </div>
  );
}

function SignalsBody({
  aggregates,
  rows,
  onDrill,
}: {
  aggregates: SignalsAggregates;
  rows: MerchantSignalRow[];
  onDrill: (cohort: Cohort) => void;
}) {
  const activated = aggregates.segments.reduce(
    (sum, segment) => sum + segment.activated,
    0,
  );
  const merchants = aggregates.meta.merchants;
  const activationRate = merchants > 0 ? activated / merchants : 0;

  const icpShare =
    merchants > 0
      ? aggregates.tiers
          .filter((tier) => tier.tier === 1 || tier.tier === 2)
          .reduce((sum, tier) => sum + tier.merchants, 0) / merchants
      : 0;

  const segmentBars: BarItem[] = useMemo(
    () =>
      [...aggregates.segments]
        .sort((a, b) => b.merchants - a.merchants)
        .map((segment) => ({
          key: segment.segment,
          label: segment.label,
          value: segment.merchants,
          share: segment.share,
          meta: formatTier(segment.tier),
          color: tierColor(segment.tier),
        })),
    [aggregates.segments],
  );

  const tierBars: BarItem[] = useMemo(
    () =>
      aggregates.tiers.map((tier) => ({
        key: String(tier.tier),
        label: tier.label,
        value: tier.merchants,
        share: tier.share,
        meta: `${formatPercent(tier.activationRate)} act.`,
        color: tierColor(tier.tier),
      })),
    [aggregates.tiers],
  );

  const channelBars = toBars(aggregates.channels, formatChannel);
  const countryBars = toBars(aggregates.countries, formatCountry);
  const shapeBars = toBars(aggregates.businessShapes, titleCase);

  const unmapped = Object.entries(aggregates.unmappedMccCodes)
    .sort((a, b) => b[1] - a[1])
    .map(([code, count]) => ({
      key: code,
      label: `MCC ${code}`,
      value: count,
    }));

  const medianDays = medianOf(
    aggregates.segments
      .map((segment) => segment.medianDaysToFirstTxn)
      .filter((value): value is number => value !== null),
  );

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
        <StatTile
          label="Merchants"
          value={formatCount(merchants)}
          hint="Lifetime, all merchants"
        />
        <StatTile
          label="Activated"
          value={formatPercent(activationRate)}
          hint={`${formatCount(activated)} have transacted`}
        />
        <StatTile
          label="GPV"
          value={formatGpv(aggregates.gpvByCurrency)}
          hint="Lifetime, succeeded payments"
        />
        <StatTile
          label="Median to first txn"
          value={formatDays(medianDays)}
          hint="Across segments"
        />
        <StatTile
          label="ICP core"
          value={formatPercent(icpShare)}
          hint="Tier 1 + 2 of the base"
        />
      </div>

      {aggregates.plans && (
        <div className="grid grid-cols-2 gap-4">
          <StatTile
            label="Free"
            value={formatCount(aggregates.plans.free)}
            hint={`${formatPercent(merchants > 0 ? aggregates.plans.free / merchants : 0)} of merchants`}
            onSelect={() => onDrill(planCohort("free"))}
          />
          <StatTile
            label="Pro"
            value={formatCount(aggregates.plans.pro)}
            hint={formatTermSplit(aggregates.plans.proByTerm)}
            onSelect={() => onDrill(planCohort("pro"))}
          />
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        <SignalsCard
          title="Segment mix"
          subtitle="Who is signing up, by curated segment"
        >
          <BarList
            items={segmentBars}
            onSelect={(item) =>
              onDrill({ label: item.label, filter: { segment: item.key } })
            }
          />
        </SignalsCard>

        <div className="space-y-6">
          <SignalsCard title="ICP tiers" subtitle="Fit of the base to the ICP">
            <BarList
              items={tierBars}
              onSelect={(item) => {
                const tier = aggregates.tiers.find(
                  (candidate) => String(candidate.tier) === item.key,
                );
                if (tier) {
                  onDrill({ label: tier.label, filter: { tier: tier.tier } });
                }
              }}
            />
          </SignalsCard>

          <SignalsCard title="Signups by month">
            <TrendBars
              points={aggregates.signupsByMonth.map((point) => ({
                key: point.month,
                label: formatMonth(point.month),
                value: point.total,
              }))}
              onSelect={(point) =>
                onDrill({
                  label: `Signed up ${point.label}`,
                  filter: { signupMonth: point.key },
                })
              }
            />
          </SignalsCard>
        </div>
      </div>

      <ConversionCard
        segments={aggregates.segments}
        baseActivationRate={activationRate}
        onDrill={onDrill}
      />

      <div className="grid gap-6 lg:grid-cols-2">
        <StallMapCard aggregates={aggregates} onDrill={onDrill} />

        <div className="space-y-6">
          <EmergingSegmentsCard
            segments={aggregates.emergingSegments}
            onDrill={onDrill}
          />
          <AdvocateCandidatesCard
            businessIds={aggregates.advocateCandidates}
            rows={rows}
            onDrill={onDrill}
          />
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <SignalsCard
          title="Acquisition channel"
          subtitle="First-touch attribution at signup"
        >
          <BarList
            items={channelBars}
            onSelect={(item) =>
              onDrill({ label: item.label, filter: { channel: item.key } })
            }
            emptyMessage="No attribution recorded"
          />
        </SignalsCard>

        <SignalsCard title="Country">
          <BarList
            items={countryBars}
            onSelect={(item) =>
              onDrill({ label: item.label, filter: { country: item.key } })
            }
          />
        </SignalsCard>

        <SignalsCard
          title="Business shape"
          subtitle="Solo operator vs registered company"
        >
          <BarList
            items={shapeBars}
            onSelect={(item) =>
              onDrill({
                label: item.label,
                filter: { businessShape: item.key },
              })
            }
          />
        </SignalsCard>
      </div>

      {unmapped.length > 0 && (
        <SignalsCard
          title="Unmapped MCCs"
          subtitle="Codes the taxonomy does not cover — candidates for a new segment"
        >
          <BarList
            items={unmapped}
            onSelect={(item) =>
              onDrill({ label: item.label, filter: { mccCode: item.key } })
            }
          />
        </SignalsCard>
      )}
    </div>
  );
}

function toBars(
  counts: Record<string, number>,
  label: (key: string) => string,
): BarItem[] {
  const total = Object.values(counts).reduce((sum, value) => sum + value, 0);
  return Object.entries(counts)
    .sort((a, b) => b[1] - a[1])
    .map(([key, value]) => ({
      key,
      label: label(key),
      value,
      share: total > 0 ? value / total : 0,
    }));
}

function medianOf(values: number[]): number | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  const middle = sorted.slice(sorted.length % 2 === 0 ? mid - 1 : mid, mid + 1);
  return middle.reduce((sum, value) => sum + value, 0) / middle.length;
}
